import React, { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { createBooking, getUserDiscount } from '../../services/supabase';
import { getCurrentUser } from '../../services/telegram-auth';

// Интерфейс для выбранного оборудования
interface SelectedEquipment {
    id: string;
    name: string;
    quantity: number;
    dailyRate: number;
    imageUrl?: string;
}

// Интерфейс для пропсов компонента подтверждения бронирования
interface BookingConfirmationProps {
    selectedEquipment: SelectedEquipment[];
    startDate: Date;
    endDate: Date;
    onCancel: () => void;
    onSuccess?: (bookingId: string) => void;
    applyDiscount?: boolean;
}

const BookingConfirmation: React.FC<BookingConfirmationProps> = ({
    selectedEquipment,
    startDate,
    endDate,
    onCancel,
    onSuccess,
    applyDiscount = true
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [projectName, setProjectName] = useState<string>('');
    const [userDiscount, setUserDiscount] = useState<number>(0);
    const navigate = useNavigate();

    // Проверяем авторизацию при монтировании компонента
    useEffect(() => {
        const currentUser = getCurrentUser();
        if (!currentUser) {
            // Если пользователь не авторизован, перенаправляем на страницу входа
            setError('Для бронирования необходимо авторизоваться');
            // Сохраняем URL текущей страницы для возврата после авторизации
            localStorage.setItem('redirectAfterLogin', window.location.pathname);
            // Перенаправляем на страницу входа после небольшой задержки
            setTimeout(() => {
                navigate('/login');
            }, 2000);
        }
    }, [navigate]);

    // Получаем информацию о скидке пользователя при монтировании компонента
    useEffect(() => {
        const fetchUserDiscount = async () => {
            try {
                const { data, error } = await getUserDiscount();
                if (error) {
                    console.error('Ошибка при получении скидки:', error);
                } else if (data !== undefined) {
                    setUserDiscount(data);
                    console.log('Скидка пользователя в BookingConfirmation:', data);
                }
            } catch (error) {
                console.error('Ошибка при получении скидки:', error);
            }
        };
        
        fetchUserDiscount();
    }, []);

    // Форматирование дат
    const formattedStartDate = format(startDate, 'dd MMMM yyyy', { locale: ru });
    const formattedEndDate = format(endDate, 'dd MMMM yyyy', { locale: ru });

    // Расчет длительности аренды в днях
    const rentalDays = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) || 1;

    // Расчет общей стоимости без скидки
    const originalPrice = selectedEquipment.reduce((sum, item) => 
        sum + (item.dailyRate * item.quantity * rentalDays), 0);
    
    // Расчет суммы скидки
    const discountAmount = (userDiscount > 0 && applyDiscount) ? Math.round(originalPrice * userDiscount / 100) : 0;
    
    // Расчет итоговой стоимости со скидкой
    const totalPrice = originalPrice - discountAmount;

    // Обработчик подтверждения бронирования
    const handleConfirm = async () => {
        try {
            // Повторно проверяем авторизацию перед отправкой запроса
            const currentUser = getCurrentUser();
            if (!currentUser) {
                setError('Для бронирования необходимо авторизоваться');
                // Сохраняем URL текущей страницы для возврата после авторизации
                localStorage.setItem('redirectAfterLogin', window.location.pathname);
                // Перенаправляем на страницу входа
                navigate('/login');
                return;
            }

            setIsSubmitting(true);
            setError(null);

            // Получаем ID пользователя из данных авторизации
            const userId = currentUser.id;

            // Форматируем даты с правильным временем для начала и конца дней
            const bookingStartDate = new Date(startDate);
            const bookingEndDate = new Date(endDate);

            // Устанавливаем время 00:01:00 для даты начала (чтобы избежать ошибки с "датой в прошлом")
            bookingStartDate.setHours(0, 1, 0, 0);

            // Устанавливаем время 23:59:59 для даты конца
            bookingEndDate.setHours(23, 59, 59, 999);

            // Форматирование дат для Supabase (ISO формат)
            const formattedStartIso = bookingStartDate.toISOString();
            const formattedEndIso = bookingEndDate.toISOString();

            // Подготовка данных для бронирования
            const bookingData = {
                user_id: userId,
                start_date: formattedStartIso,
                end_date: formattedEndIso,
                total_price: totalPrice,
                guests: 1,
                project_name: projectName.trim() || null,
                equipment_data: JSON.stringify(selectedEquipment.map(item => ({
                    equipment_id: item.id,
                    quantity: item.quantity,
                    daily_rate: item.dailyRate
                })))
            };

            console.log('Отправка данных бронирования:', bookingData);

            // Отправка данных в Supabase
            const { data, error } = await createBooking(bookingData);

            if (error) {
                console.error('Ошибка при создании бронирования:', error);
                setError('Произошла ошибка при создании бронирования. Пожалуйста, попробуйте позже.');
                return;
            }

            console.log('Бронирование успешно создано:', data);

            // Вызываем колбэк успешного бронирования, если он предоставлен
            if (onSuccess && data?.booking_id) {
                onSuccess(data.booking_id);
            } else {
                // Перенаправляем на страницу успешного бронирования
                navigate('/booking-success', {
                    state: {
                        bookingId: data?.booking_id,
                        startDate: formattedStartDate,
                        endDate: formattedEndDate,
                        totalPrice,
                        selectedEquipment
                    }
                });
            }
        } catch (err) {
            console.error('Непредвиденная ошибка при бронировании:', err);
            setError('Непредвиденная ошибка при создании бронирования. Пожалуйста, попробуйте позже.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-4 max-h-[90vh] overflow-y-auto">
                <h2 className="text-xl font-bold mb-3">Подтверждение бронирования</h2>

                <div className="mb-4 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <p className="text-sm text-gray-600">Дата начала:</p>
                            <p className="font-medium text-sm">{formattedStartDate}</p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">Дата окончания:</p>
                            <p className="font-medium text-sm">{formattedEndDate}</p>
                        </div>
                    </div>

                    <div>
                        <p className="text-sm text-gray-600">Срок аренды:</p>
                        <p className="font-medium text-sm">{rentalDays} {rentalDays === 1 ? 'день' : rentalDays < 5 ? 'дня' : 'дней'}</p>
                    </div>

                    <div>
                        <label htmlFor="projectName" className="block text-sm text-gray-600">
                            Название проекта (необязательно):
                        </label>
                        <input
                            type="text"
                            id="projectName"
                            className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-300 bg-white text-sm mt-1"
                            placeholder="Введите название проекта"
                            value={projectName}
                            onChange={(e) => setProjectName(e.target.value)}
                        />
                    </div>
                </div>

                <div className="mb-3">
                    <h3 className="text-sm font-semibold mb-1 text-gray-600">Выбранное оборудование:</h3>
                    <ul className="space-y-1">
                        {selectedEquipment.map((item) => (
                            <li key={item.id} className="border-b border-gray-100 pb-1">
                                <div className="flex justify-between text-sm">
                                    <span className="font-medium">{item.name}</span>
                                    <span>x{item.quantity}</span>
                                </div>
                                <div className="text-xs text-gray-500">
                                    {item.dailyRate.toLocaleString('ru-RU')} ₽/день × {item.quantity} × {rentalDays} = {(item.dailyRate * item.quantity * rentalDays).toLocaleString('ru-RU')} ₽
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="mb-4 bg-gray-50 p-2 rounded-md">
                    {userDiscount > 0 && (
                        <div className="flex justify-between text-sm mb-1">
                            <span>Сумма без скидки:</span>
                            <span className="text-gray-500">{originalPrice.toLocaleString('ru-RU')} ₽</span>
                        </div>
                    )}
                    
                    {userDiscount > 0 && (
                        <div className="flex justify-between text-sm mb-1 text-green-600">
                            <span>Скидка ({userDiscount}%):</span>
                            <span>-{discountAmount.toLocaleString('ru-RU')} ₽</span>
                        </div>
                    )}
                    
                    <div className="flex justify-between font-bold">
                        <span>Итого:</span>
                        <span>{totalPrice.toLocaleString('ru-RU')} ₽</span>
                    </div>
                </div>

                {error && (
                    <div className="mb-3 p-2 bg-red-50 text-red-700 rounded-md text-sm">
                        {error}
                    </div>
                )}

                <div className="flex justify-between">
                    <Button
                        variant="outline"
                        onClick={onCancel}
                        disabled={isSubmitting}
                        className="text-sm py-1 px-3"
                    >
                        Отменить
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={isSubmitting}
                        className="text-sm py-1 px-3"
                    >
                        {isSubmitting ? 'Обработка...' : 'Подтвердить бронирование'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default BookingConfirmation; 